import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { siteConfig } from "@/config/siteConfig";
import {
  BLOG_POSTS,
  CATEGORIES,
  categoryToSlug,
  slugToCategory,
  sortPosts,
} from "@/data/blogData";
import { Newspaper, Clock } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" });
}

const BlogIndex = () => {
  const [params, setParams] = useSearchParams();
  const activeCategorySlug = params.get("category") || "";
  const activeCategory = activeCategorySlug ? slugToCategory(activeCategorySlug) : null;

  const visible = useMemo(() => {
    const filtered = activeCategory
      ? BLOG_POSTS.filter((p) => p.category === activeCategory)
      : BLOG_POSTS;
    return sortPosts(filtered);
  }, [activeCategory]);

  const setCategory = (label) => {
    const p = new URLSearchParams(params);
    if (!label) p.delete("category");
    else p.set("category", categoryToSlug(label));
    setParams(p, { replace: true });
  };

  const path = "/blog";

  return (
    <>
      <SEO
        rawTitle={`Knowledge Centre — Pharma Printing & Coding | ${siteConfig.COMPANY_NAME}`}
        description="Technical articles on pharmaceutical printing inks, coding consumables, substrate selection, production trials and supply chain for pharmaceutical manufacturers."
        path={path}
        ogImage={`${siteConfig.DOMAIN}/images/home/hero-blister-line.jpg`}
      />
      <Helmet>
      </Helmet>

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Knowledge Centre" }]} />
      </div>

      {/* Hero */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-6"
        data-testid="blog-hero"
      >
        <p className="eyebrow">Knowledge Centre</p>
        <h1
          className="font-heading mt-4 text-[32px] leading-tight lg:text-[48px] lg:leading-[1.05] font-semibold text-[var(--navy)] tracking-tight max-w-3xl"
          data-testid="blog-h1"
        >
          Insights on Pharmaceutical Printing &amp; Coding
        </h1>
        <p className="mt-6 text-[16px] lg:text-[18px] text-[var(--charcoal)] max-w-3xl leading-relaxed">
          Practical technical guidance on printing inks, coding consumables and production applications for pharmaceutical manufacturing.
        </p>
      </section>

      {/* Category chips */}
      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 pb-2" data-testid="blog-filters">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCategory("")}
            className={`text-[12px] uppercase tracking-[0.14em] px-3.5 py-1.5 border rounded-[3px] transition-colors ${
              !activeCategory
                ? "bg-[var(--navy)] text-white border-[var(--navy)]"
                : "bg-white text-[var(--slate)] border-[var(--border)] hover:text-[var(--navy)] hover:border-[var(--navy)]"
            }`}
            data-testid="blog-filter-all"
          >
            All
          </button>
          {CATEGORIES.map((c) => {
            const active = activeCategory === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={`text-[12px] uppercase tracking-[0.14em] px-3.5 py-1.5 border rounded-[3px] transition-colors ${
                  active
                    ? "bg-[var(--navy)] text-white border-[var(--navy)]"
                    : "bg-white text-[var(--slate)] border-[var(--border)] hover:text-[var(--navy)] hover:border-[var(--navy)]"
                }`}
                data-testid={`blog-filter-${categoryToSlug(c)}`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </section>

      {/* Grid */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-10 lg:py-14"
        data-testid="blog-grid-section"
      >
        {visible.length === 0 ? (
          <div className="border border-[var(--border)] rounded-[4px] p-10 bg-[var(--bg-alt)] text-center" data-testid="blog-empty">
            <Newspaper className="mx-auto mb-3 text-[var(--pharma-blue)]" size={28} strokeWidth={1.5} />
            <p className="text-[15px] text-[var(--charcoal)] max-w-xl mx-auto">
              No articles in this category yet. Explore other topics or{" "}
              <Link to="/contact" className="text-[var(--pharma-blue)] underline underline-offset-4">contact us to suggest a topic</Link>.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="blog-grid">
            {visible.map((p) => (
              <article
                key={p.slug}
                className="group border border-[var(--border)] rounded-[4px] p-6 bg-white flex flex-col hover:border-[var(--navy)] transition-colors"
                data-testid={`blog-card-${p.slug}`}
              >
                <Link to={`/blog/${p.slug}`} className="flex flex-col h-full" data-testid={`blog-card-link-${p.slug}`}>
                  <p className="eyebrow" style={{ color: "var(--pharma-blue)" }}>{p.category}</p>
                  <h2 className="font-heading mt-3 text-[20px] leading-snug font-semibold text-[var(--navy)] group-hover:underline underline-offset-4">
                    {p.title}
                  </h2>
                  <p className="mt-3 text-[14px] leading-relaxed text-[var(--slate)] flex-1 line-clamp-3">
                    {p.excerpt}
                  </p>
                  <div className="mt-5 pt-4 border-t border-[var(--border)] flex items-center justify-between text-[12px]">
                    {p.status === "stub" ? (
                      <span className="uppercase tracking-wider text-[var(--slate)]">Coming soon</span>
                    ) : (
                      <>
                        <span className="text-[var(--slate)]">{fmtDate(p.publishedDate)}</span>
                        <span className="inline-flex items-center gap-1 text-[var(--slate)]">
                          <Clock size={12} strokeWidth={1.6} />
                          {p.readingTimeMin} min read
                        </span>
                      </>
                    )}
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default BlogIndex;
